import { UserInfo } from '@app-types/user-info';
import type SdInput from '@solid-design-system/components/dist-versioned/components/input/input';

export interface InfoInputField<T> {
  key: keyof T;
  i18n: string;
  required: boolean;
  readonly?: boolean;
  type?: SdInput['type'];
  mode?: SdInput['inputmode'];
}

export type RelevantUserInfo = Omit<UserInfo, 'pending'>;

export interface UserInfoInputCombinedRequirement {
  keys: ReadonlyArray<keyof UserInfo>,
  errorI18n: string,
}

const commonInputs: ReadonlyArray<InfoInputField<RelevantUserInfo>> = [
  {
    key: 'salutation',
    i18n: 'profile.general.salutation',
    required: true,
  },
  {
    key: 'academicTitle',
    i18n: 'profile.general.academicTitle',
    required: false,
  },
  {
    key: 'firstName',
    i18n: 'profile.general.firstName',
    required: true,
  },
  {
    key: 'lastName',
    i18n: 'profile.general.lastName',
    required: true,
  },
];

const personalInputs: ReadonlyArray<InfoInputField<RelevantUserInfo>> = [
  {
    key: 'phoneNumber',
    i18n: 'profile.general.phoneNumber',
    type: 'tel',
    mode: 'tel',
    required: true,
  },
  {
    key: 'mailAddress',
    i18n: 'profile.general.email',
    required: true,
  },
];

const businessInputs: ReadonlyArray<InfoInputField<RelevantUserInfo>> = [
  {
    key: 'company',
    i18n: 'profile.account.organization',
    required: true,
  },
  {
    key: 'VATId',
    i18n: 'profile.account.vatId',
    required: false,
  },
  {
    key: 'city',
    i18n: 'profile.account.place',
    required: true,
  },
  {
    key: 'street',
    i18n: 'profile.account.street',
    required: true,
  },
  {
    key: 'zip',
    i18n: 'profile.account.zip',
    required: true,
  },
];

const additionalInputs: ReadonlyArray<InfoInputField<RelevantUserInfo>> = [
  {
    key: 'freetext',
    i18n: 'profile.account.freetext',
    required: false,
  }
];

const commonReadonly: ReadonlyArray<InfoInputField<RelevantUserInfo>> = commonInputs.map<InfoInputField<RelevantUserInfo>>(input => ({ ...input, readonly: true }));
const personalReadonly: ReadonlyArray<InfoInputField<RelevantUserInfo>> = personalInputs.map<InfoInputField<RelevantUserInfo>>(input => ({ ...input, readonly: true }));
const businessReadonly: ReadonlyArray<InfoInputField<RelevantUserInfo>> = businessInputs.map<InfoInputField<RelevantUserInfo>>(input => ({ ...input, readonly: true }));
const additionalReadonly: ReadonlyArray<InfoInputField<RelevantUserInfo>> = additionalInputs.map<InfoInputField<RelevantUserInfo>>(input => ({ ...input, readonly: true }));

const combinedRequiredInputs: ReadonlyArray<UserInfoInputCombinedRequirement> = [];

const _userInfoInputFields = {
  common: commonInputs,
  personal: personalInputs,
  business: businessInputs,
  additional: additionalInputs,

  commonReadonly: commonReadonly,
  personalReadonly: personalReadonly,
  businessReadonly: businessReadonly,
  additionalReadonly: additionalReadonly,

  commonI18n: 'profile.account.generalInfos',
  personalI18n: 'profile.account.personalInfos',
  businessI18n: 'profile.account.businessData',
  additionalI18n: 'profile.account.additional',

  personalCombinedRequiredInputs: combinedRequiredInputs,

  copy: function(source: UserInfo, ...copiedFields: ReadonlyArray<InfoInputField<RelevantUserInfo>>[]): UserInfo {
    const result: UserInfo = {} as UserInfo;

    for (const fields of copiedFields) {
      for (const field of fields) {
        const value: string | undefined = source[field.key];

        if (value !== undefined) {
          result[field.key] = value;
        }
      }
    }

    return result;
  }
};

export const userInfoInputFields: Readonly<typeof _userInfoInputFields> = _userInfoInputFields;
